/**
 * PWAInstall — Enregistre le service worker + auto-update.
 *
 * Comportement :
 *  - Enregistre /service-worker.js au chargement
 *  - Vérifie régulièrement s'il existe une nouvelle version (toutes les 60s + à chaque navigation)
 *  - Quand une nouvelle version est prête, la force à s'activer immédiatement (SKIP_WAITING)
 *    puis recharge la page automatiquement — l'utilisateur voit toujours la dernière version
 */
import { useEffect } from "react";
import { toast } from "sonner";

/** Nom du bundle principal chargé (ex. main.10dc4c25.js), qui change à chaque déploiement. */
const currentBundle = () => {
  const s = [...document.scripts].map((x) => x.src).find((src) => /\/static\/js\/main\.[^/]+\.js$/.test(src));
  return s ? s.split("/").pop() : null;
};

/** Le service worker ne change pas d'un déploiement à l'autre : on compare donc le bundle
 *  servi au bundle chargé, et on propose de recharger (sans forcer : une saisie peut être en cours). */
function useNewVersionNotice() {
  useEffect(() => {
    const loaded = currentBundle();
    if (!loaded) return undefined; // mode développement
    let notified = false;
    const check = async () => {
      if (notified || document.visibilityState !== "visible") return;
      try {
        const html = await (await fetch("/", { cache: "no-store" })).text();
        const m = html.match(/static\/js\/(main\.[^"']+\.js)/);
        if (m && m[1] !== loaded) {
          notified = true;
          toast("Nouvelle version de Véyèr CIEL disponible", {
            description: "Mettez à jour pour profiter des dernières améliorations (pensez à enregistrer une saisie en cours).",
            duration: Infinity,
            action: { label: "Mettre à jour", onClick: () => window.location.reload() },
          });
        }
      } catch { /* hors ligne */ }
    };
    const timer = setInterval(check, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", check);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", check); };
  }, []);
}

export default function PWAInstall() {
  useNewVersionNotice();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let refreshing = false;
    // Quand le SW contrôleur change (nouvelle version activée), on recharge la page une seule fois
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      console.info("[PWA] Nouvelle version activée — reload...");
      window.location.reload();
    });

    const promoteWaiting = (reg) => {
      if (reg.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    };

    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((reg) => {
          // Si un SW attend déjà, on le promeut immédiatement
          promoteWaiting(reg);

          reg.addEventListener("updatefound", () => {
            const newSw = reg.installing;
            if (!newSw) return;
            newSw.addEventListener("statechange", () => {
              if (newSw.state === "installed" && navigator.serviceWorker.controller) {
                console.info("[PWA] Nouvelle version installée — activation...");
                promoteWaiting(reg);
              }
            });
          });

          // Vérifier les mises à jour toutes les 60 secondes
          setInterval(() => reg.update().catch(() => {}), 60_000);
        })
        .catch((err) => console.warn("[PWA] SW registration failed:", err));
    });
  }, []);

  return null;
}
